import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {ProgressSpinner} from "primereact/progressspinner";
import {Tag} from "primereact/tag";
import Avatar from "../../../system/components/avatar";
import AvatarGroup from "../../../system/components/avatar-group";
import Icon from "../../../system/components/icon";
import Span from "../../../system/components/span";
import {useTranslate} from "../../../system/useTranslate";
import {useChat} from "../../../models/chat/use-chat";
import {useChatMessage} from "../../../models/chat-message/use-chat-message";
import UploadDialog from "../../components/upload-dialog";
import ChatComposer, {buildReferenceSuggestions, mentionToken, referenceToken, valueList} from "../../inbox/chat-composer";
import {LinkedEntityCard, MessageContent} from "../../inbox/chat-message";
import ChatMessageAttachment from "../../inbox/chat-message-attachment";
import ChatMessageReactions from "../../inbox/chat-message-reactions";
import {chatMessageAttachmentIdentifiers} from "../../inbox/chat-message-attachment-payload";
import {entityKey, isActivityItem, sameEntity} from "../../inbox/inbox-utils";
import {sortMessagesChronologically} from "../../inbox/conversation-message-utils";
import {richTextToPlain} from "../../../system/components/rich-text";

const EMPTY_DRAFT = {text: "", mentions: [], references: []};
const STATUS_SEVERITY = {"on-way": "success", "has-risks": "warning", "out-of-way": "danger", achieved: "success", canceled: "secondary", archived: "secondary"};

function nameOf(membership, translate) {
    const member = membership?.member || membership;
    return member?.fullname || [member?.firstname, member?.lastname].filter(Boolean).join(" ")
        || member?.username || translate("project.messages.unknown-member");
}

function changeDraft(draft, text) {
    return {
        ...draft,
        text,
        mentions: draft.mentions.filter((item) => text.includes(`@${item.token || mentionToken(item)}`)),
        references: draft.references.filter((item) => text.includes(`/${item.token || referenceToken(item.type, item.title)}`)),
    };
}

function selectSuggestion(draft, suggestion) {
    if (suggestion.suggestionType === "mention") {
        const item = {...suggestion, token: suggestion.insertText};
        return {...draft, mentions: draft.mentions.some((value) => String(value.id) === String(item.id))
            ? draft.mentions : [...draft.mentions, item]};
    }
    const item = {type: suggestion.type, id: suggestion.id, documentId: suggestion.documentId, title: suggestion.title, token: suggestion.insertText};
    return {...draft, references: draft.references.some((value) => value.type === item.type && sameEntity(value, item))
        ? draft.references : [...draft.references, item]};
}

function parseValue(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    if (typeof value !== "string") return [value];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [parsed].filter(Boolean);
    } catch {
        return value.split("\n").map((item) => item.trim()).filter(Boolean);
    }
}

export function decisionText(value) {
    if (value === null || value === undefined) return "";
    if (["string", "number", "boolean"].includes(typeof value)) return String(value).trim();
    if (Array.isArray(value)) return value.map(decisionText).filter(Boolean).join("، ");
    if (typeof value === "object") {
        const preferred = value.title ?? value.text ?? value.label ?? value.name ?? value.value ?? value.description;
        if (preferred !== undefined && preferred !== value) return decisionText(preferred);
        return Object.values(value).map(decisionText).filter(Boolean).join("، ");
    }
    return "";
}

function scopedEventMatches(item, entityData, scopeType) {
    const key = String(entityKey(entityData) || "");
    if (!key || !isActivityItem(item)) return false;
    const candidates = [item?.sourceDocumentId, item?.payload?.sourceDocumentId, item?.payload?.projectDocumentId,
        item?.payload?.teamDocumentId, item?.payload?.project?.documentId, item?.payload?.team?.documentId,
        item?.payload?.contextDocumentId].filter(Boolean).map(String);
    if (item?.sourceType && item.sourceType !== scopeType && !candidates.includes(key)) return false;
    return candidates.includes(key) || String(item?.actionUrl || "").includes(key);
}

function memberForMention(mention, members) {
    return (members || []).find((member) => String(member?.id) === String(mention?.id)
        || String(member?.documentId) === String(mention?.documentId)) || mention;
}

function AvatarStack({members, limit = 4, size = "normal"}) {
    const avatars = Array.from(valueList(members).reduce((unique, value, index) => {
        const member = value?.member || value;
        const identity = entityKey(member) || member?.username || member?.email || member?.fullname
            || [member?.firstname, member?.lastname].filter(Boolean).join(" ") || `member-${index}`;
        if (!unique.has(String(identity))) unique.set(String(identity), value);
        return unique;
    }, new Map()).entries());
    if (!avatars.length) return null;
    return <AvatarGroup dir="ltr" maxVisible={limit}>
        {avatars.map(([identity, member]) => <Avatar
            key={identity}
            size={size}
            memberData={member?.member || member}
        />)}
    </AvatarGroup>;
}

function MessageBody({item, workspaceId}) {
    return <div className="flex flex-column gap-3">
        {item?.messageContent && <MessageContent content={item.messageContent} mentions={item.mentions}/>}
        {valueList(item?.linkedEntity).map((reference) => <LinkedEntityCard
            key={`${reference.type}-${reference.documentId || reference.id}`} reference={reference}/>)}
        {valueList(item?.attachment).map((file) => <ChatMessageAttachment
            key={file?.documentId || file?.id} file={file} workspaceId={workspaceId}/>)}
    </div>;
}

export function systemEventText(item, translate) {
    const value = item?.message || item?.title || item?.eventKind || "";
    return value && typeof translate === "function" ? translate(value) : value;
}

function SystemEvent({item}) {
    const translate = useTranslate();
    return <div className="flex align-items-center gap-3 py-2 text-500" role="status">
        <span className="flex-1 border-top-1 border-200"/>
        <span className="flex align-items-center gap-2 text-xs white-space-nowrap">
            <span className="w-2rem h-2rem border-circle surface-100 flex align-items-center justify-content-center">
                <Icon icon="mi mi:notifications" className="text-primary"/>
            </span>
            <span>{systemEventText(item, translate)}</span>
            <Span spanClassName="text-400" type="PERSIAN_DATE" format="HH:mm">{item?.createdAt}</Span>
        </span>
        <span className="flex-1 border-top-1 border-200"/>
    </div>;
}

function ScopeSummaryCard({entityData, scopedTasks, members, teamMode}) {
    const translate = useTranslate();
    const timeline = parseValue(entityData?.timeline);
    const latest = timeline[timeline.length - 1] || {};
    const summary = richTextToPlain(entityData?.briff || latest?.briff || entityData?.description);
    const decisions = parseValue(teamMode ? entityData?.teamRules : entityData?.decisions || latest?.decisions).slice(0, 3);
    const nextTasks = scopedTasks.filter((task) => !["done", "completed", "achieved", "canceled"].includes(task?.taskStatus)).slice(0, 3);
    const owner = entityData?.owner?.member || entityData?.owner;
    const doneCount = scopedTasks.filter((task) => task?.taskStatus === "done").length;
    const progress = scopedTasks.length ? Math.round(doneCount / scopedTasks.length * 100) : null;

    return <article className="surface-card border-1 border-200 border-round-xl shadow-2 overflow-hidden">
        <header className="p-4 md:p-5 border-bottom-1 border-100 flex align-items-center gap-3">
            <span className="w-3rem h-3rem border-round-lg bg-orange-50 flex align-items-center justify-content-center flex-shrink-0">
                <Icon icon="mi mi:assignment" className="text-2xl text-primary"/>
            </span>
            <div className="flex-1 min-w-0">
                <strong className="block text-base text-900 mb-1">{translate(teamMode ? "team.messages.status-card-title" : "project.messages.status-card-title")}</strong>
                <small className="text-xs text-500">{entityData?.name || entityData?.title}</small>
            </div>
            {!teamMode && <Tag rounded severity={STATUS_SEVERITY[entityData?.projectStatus] || "info"}
                value={translate(entityData?.projectStatus || "not-selected")}/>}
        </header>
        <div className="p-4 md:p-5 flex flex-column gap-4">
            <div className="flex align-items-center gap-2">
                <Avatar size="normal" memberData={owner}/>
                <div className="flex flex-column">
                    <small className="text-xs text-500">{translate(teamMode ? "team.messages.team-owner" : "project.messages.project-owner")}</small>
                    <strong className="text-xs text-800">{nameOf(owner, translate)}</strong>
                </div>
            </div>
            <section>
                <h3 className="text-sm text-800 mt-0 mb-2">{translate("project.messages.summary")}</h3>
                <p className="m-0 surface-50 border-round-lg p-3 text-xs text-700 line-height-3">
                    {summary || translate(teamMode ? "team.messages.no-summary" : "project.messages.no-summary")}
                </p>
            </section>
            <div className="grid">
                <section className="col-12 md:col-6">
                    <h3 className="text-sm text-800 mt-0 mb-2">{translate("project.messages.next-steps")}</h3>
                    <div className="flex flex-column gap-2">
                        {nextTasks.length ? nextTasks.map((task) => <div key={entityKey(task)} className="flex align-items-center gap-2 text-xs text-700">
                            <Icon icon="mi mi:check_box_outline_blank" className="text-primary"/><span>{task.title}</span>
                        </div>) : <small className="text-xs text-400">{translate("project.messages.no-next-steps")}</small>}
                    </div>
                </section>
                <section className="col-12 md:col-6">
                    <h3 className="text-sm text-800 mt-0 mb-2">{translate("project.messages.decisions")}</h3>
                    <div className="flex flex-column gap-2">
                        {decisions.map(decisionText).filter(Boolean).length ? decisions.map(decisionText).filter(Boolean).map((decision, index) => <div key={`${decision}-${index}`} className="flex align-items-start gap-2 text-xs text-700">
                            <Icon icon="mi mi:verified" className="text-green-500"/><span>{decision}</span>
                        </div>) : <small className="text-xs text-400">{translate("project.messages.no-decisions")}</small>}
                    </div>
                </section>
            </div>
        </div>
        <footer className="surface-50 border-top-1 border-100 px-4 md:px-5 py-3 flex align-items-center justify-content-between gap-3">
            <AvatarStack members={members}/>
            <span className="flex align-items-center gap-2 text-xs text-500"><Icon icon="mi mi:monitoring" className="text-primary"/>
                {translate(teamMode ? "team.messages.progress" : "project.messages.progress")}: {progress === null ? "—" : `${progress}%`}</span>
        </footer>
    </article>;
}

function MessageCard({item, replies, membership, scopeData, teamMode, mentionMembers, references, open, canReply, draft, sending, reacting, onToggle, onDraft, onSelect, onSubmit, onReact}) {
    const translate = useTranslate();
    const mentionedMembers = valueList(item?.mentions).map((mention) => memberForMention(mention, mentionMembers));
    const isQuestion = /[؟?]\s*$/.test(String(item?.messageContent || "").trim());
    const isOwner = sameEntity(item?.user, scopeData?.owner);
    const threadVisible = open || replies.length > 0;

    return <article className="surface-card border-1 border-200 border-round-2xl p-4 md:p-5 shadow-2">
        <header className="flex align-items-start justify-content-between gap-3">
            <div className="flex align-items-center gap-3 min-w-0">
                <Avatar size="large" memberData={item?.user?.member}/>
                <div className="min-w-0">
                    <div className="flex flex-wrap align-items-center gap-2 mb-1">
                        <strong className="text-sm text-900">{nameOf(item?.user, translate)}</strong>
                        <Tag rounded severity={isQuestion ? "warning" : "info"}
                            value={translate(isQuestion ? "project.messages.type.question" : "project.messages.type.update")} className="text-xs"/>
                    </div>
                    <small className="text-xs text-500">{translate(teamMode
                        ? (isOwner ? "team.messages.role.owner" : "team.messages.role.member")
                        : (isOwner ? "project.messages.role.owner" : "project.messages.role.member"))}</small>
                </div>
            </div>
            <div className="flex align-items-center gap-2 flex-shrink-0">
                <span className="surface-100 border-round-3xl px-2 py-1 text-xs text-500">
                    <Span type="PERSIAN_DATE" format="HH:mm">{item?.sentAt || item?.createdAt}</Span>
                </span>
                <Icon icon="mi mi:notifications_off" className="text-400"/>
            </div>
        </header>
        <div className="mr-6 md:mr-7 mt-4">
            <div className="surface-50 border-1 border-100 border-round-xl p-3 md:p-4">
                <MessageBody item={item} workspaceId={membership?.workspace?.documentId}/>
            </div>
            {!!mentionedMembers.length && <div className="flex align-items-center gap-3 mt-3">
                <small className="text-xs text-500">{translate("project.messages.mentioned-members")}</small>
                <AvatarStack members={mentionedMembers} size="normal"/>
            </div>}
            <div className="flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top-1 border-100">
                {!!replies.length && <div className="flex align-items-center gap-2 ml-2">
                    <AvatarStack members={replies.map((reply) => reply?.user?.member)} limit={3}/>
                    <small className="text-xs text-500">{replies.length} {translate("project.messages.replies")}</small>
                </div>}
                <Button text severity="secondary" icon={<Icon icon="mi mi:reply" className="text-sm"/>}
                    label={translate("project.messages.reply")} className="text-xs gap-2 px-2 py-1" onClick={onToggle}/>
                <ChatMessageReactions message={item} currentMembership={membership} loading={reacting} onReact={onReact}/>
            </div>
            {threadVisible && <section className="border-right-2 border-200 pr-3 md:pr-4 mt-3 flex flex-column gap-3">
                {replies.map((reply) => <div key={entityKey(reply)} className="flex gap-2">
                    <Avatar size="normal" memberData={reply?.user?.member}/>
                    <div className="flex-1 min-w-0 surface-50 border-round-xl p-3">
                        <div className="flex flex-wrap align-items-center gap-2 mb-2"><strong className="text-xs text-800">{nameOf(reply?.user, translate)}</strong>
                            <Span spanClassName="text-xs text-400" type="PERSIAN_DATE" format="HH:mm">{reply?.sentAt || reply?.createdAt}</Span></div>
                        <MessageBody item={reply} workspaceId={membership?.workspace?.documentId}/>
                    </div>
                </div>)}
                {canReply && open && <ChatComposer value={draft.text} members={mentionMembers} references={references}
                    onChange={(event) => onDraft(event.target.value)} onSelect={({suggestion}) => onSelect(suggestion)} onSubmit={onSubmit}
                    submitDisabled={!draft.text.trim() && !draft.references.length} loading={sending} disabled={sending}
                    showQuickActions={false} placeholder={translate("inbox.composer.reply-placeholder")}/>}
            </section>}
        </div>
    </article>;
}

function Message({projectData, teamData, projectAccess, teamAccess}) {
    const translate = useTranslate();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const {chatMessages, tasks, projects, goals, teams, customers, inboxItems} = useSelector((state) => state.workspaceGraph);
    const chatApi = useChat();
    const messageApi = useChatMessage();
    const [chat, setChat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [replyDrafts, setReplyDrafts] = useState({});
    const [openReplies, setOpenReplies] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [uploadVisible, setUploadVisible] = useState(false);
    const [reactingKey, setReactingKey] = useState(null);
    const teamMode = Boolean(teamData);
    const scopeData = teamData || projectData;
    const scopeAccess = teamMode ? teamAccess : projectAccess;
    const scopeType = teamMode ? "team" : "project";

    const chatMemberships = useMemo(() => valueList(chat?.users), [chat?.users]);
    const mentionMembers = useMemo(() => chatMemberships.map((item) => item?.member).filter((member) => member?.id), [chatMemberships]);
    const references = useMemo(() => buildReferenceSuggestions({tasks, projects, goals, teams, customers}, translate), [customers, goals, projects, tasks, teams, translate]);
    const scopedTasks = useMemo(() => (tasks || []).filter((task) => {
        if (!teamMode) return sameEntity(task?.project, projectData)
            || valueList(task?.projects).some((project) => sameEntity(project, projectData));
        const teamProjects = valueList(teamData?.projects);
        return valueList(task?.projects).some((project) => teamProjects.some((item) => sameEntity(item, project)));
    }), [projectData, tasks, teamData, teamMode]);

    useEffect(() => {
        if (!scopeData?.documentId) return;
        let cancelled = false;
        setLoading(true);
        const request = teamMode ? chatApi.getTeamChat(scopeData.documentId) : chatApi.getProjectChat(scopeData.documentId);
        request.then(async (result) => {
            if (!result || cancelled) return;
            setChat(result);
            await Promise.all([messageApi.getChatMessagePage(result, {page: 1, pageSize: 100, replace: true}), chatApi.markChatRead(entityKey(result))]);
        }).catch((requestError) => !cancelled && setError(requestError?.message || (teamMode ? "team.messages.load-error" : "project.messages.load-error")))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
        // API hook methods intentionally have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeData?.documentId, teamMode]);

    const messages = useMemo(() => sortMessagesChronologically((chatMessages || []).filter((item) => sameEntity(item?.chat, chat))), [chat, chatMessages]);
    const replies = useMemo(() => messages.reduce((result, item) => {
        const parent = entityKey(item?.parent);
        if (parent) result[parent] = [...(result[parent] || []), item];
        return result;
    }, {}), [messages]);
    const roots = useMemo(() => messages.filter((item) => !entityKey(item?.parent)).reverse(), [messages]);
    const events = useMemo(() => (inboxItems || [])
        .filter((item) => scopedEventMatches(item, scopeData, scopeType))
        .sort((left, right) => new Date(right?.createdAt || 0) - new Date(left?.createdAt || 0)),
    [inboxItems, scopeData, scopeType]);
    const feedItems = useMemo(() => [...roots.map((item) => ({type: "message", item, date: item?.sentAt || item?.createdAt})),
        ...events.map((item) => ({type: "event", item, date: item?.createdAt}))]
        .sort((left, right) => new Date(right.date || 0) - new Date(left.date || 0)), [events, roots]);

    const send = async (currentDraft, parent, files = []) => {
        if (!chat || scopeAccess?.isReadOnly || (!currentDraft.text.trim() && !currentDraft.references.length && !files.length)) return;
        setSending(true);
        try {
            await messageApi.createChatMessage({chat: entityKey(chat), messageContent: currentDraft.text.trim() || null,
                mentions: currentDraft.mentions.map((item) => ({id: item.id, token: item.token})),
                linkedEntity: currentDraft.references.map((item) => ({type: item.type, id: item.id, documentId: item.documentId, token: item.token})),
                attachment: files.length ? chatMessageAttachmentIdentifiers(files) : undefined,
                parent: parent ? entityKey(parent) : null, clientNonce: `${Date.now()}-${Math.random().toString(16).slice(2)}`});
            if (parent) {
                const key = String(entityKey(parent));
                setReplyDrafts((current) => ({...current, [key]: EMPTY_DRAFT}));
                setOpenReplies((current) => ({...current, [key]: true}));
            } else { setDraft(EMPTY_DRAFT); setAttachments([]); }
        } catch (requestError) { setError(requestError?.message || (teamMode ? "team.messages.send-error" : "project.messages.send-error")); }
        finally { setSending(false); }
    };

    const react = async (item, emoji) => {
        setReactingKey(entityKey(item));
        try { await messageApi.setChatMessageReaction(item, emoji); }
        finally { setReactingKey(null); }
    };

    if (loading) return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner/></div>;

    return <div className="h-full min-h-0 flex flex-column surface-ground">
        <div className="flex-1 min-h-0 overflow-auto p-3 md:p-5">
            <div className="mx-auto flex flex-column gap-4 max-w-60rem">
                <ScopeSummaryCard entityData={scopeData} scopedTasks={scopedTasks} members={mentionMembers} teamMode={teamMode}/>
                {!feedItems.length ? <div className="surface-card border-1 border-200 border-round-xl p-6 text-center text-500 shadow-1">
                    <Icon icon="mi mi:forum" className="text-4xl text-300 mb-2"/><div className="text-sm font-medium">{translate(teamMode ? "team.messages.empty" : "project.messages.empty")}</div>
                    <small className="text-xs">{translate(teamMode ? "team.messages.empty-help" : "project.messages.empty-help")}</small>
                </div> : feedItems.map(({type, item}) => {
                    if (type === "event") return <SystemEvent key={`event-${entityKey(item)}`} item={item}/>;
                    const key = String(entityKey(item));
                    const replyDraft = replyDrafts[key] || EMPTY_DRAFT;
                    return <MessageCard key={`message-${key}`} item={item} replies={replies[key] || []} membership={workspaceMembershipData}
                        scopeData={scopeData} teamMode={teamMode} mentionMembers={mentionMembers} references={references} open={Boolean(openReplies[key])}
                        canReply={!scopeAccess?.isReadOnly} draft={replyDraft} sending={sending} reacting={String(reactingKey) === key}
                        onToggle={() => setOpenReplies((current) => ({...current, [key]: !current[key]}))}
                        onDraft={(text) => setReplyDrafts((current) => ({...current, [key]: changeDraft(current[key] || EMPTY_DRAFT, text)}))}
                        onSelect={(suggestion) => setReplyDrafts((current) => ({...current, [key]: selectSuggestion(current[key] || EMPTY_DRAFT, suggestion)}))}
                        onSubmit={() => send(replyDraft, item)} onReact={react}/>;
                })}
            </div>
        </div>

        <div className="surface-card border-top-2 border-200 p-3 md:p-4 shadow-6 flex-shrink-0">
            <div className="mx-auto max-w-60rem">
                {!scopeAccess?.isReadOnly ? <ChatComposer value={draft.text} members={mentionMembers} references={references} attachments={attachments}
                    onChange={(event) => setDraft((current) => changeDraft(current, event.target.value))}
                    onSelect={({suggestion}) => setDraft((current) => selectSuggestion(current, suggestion))}
                    onRemoveAttachment={(file) => setAttachments((current) => current.filter((item) => !sameEntity(item, file)))}
                    canAttach attachInActionBar emojiInActionBar quickActionsIconOnly quickActionTypes={["goal", "task", "project", "team", "mention"]}
                    submitLabel={translate("inbox.composer.send")} className="shadow-2 border-round-xl" actionBarClassName="surface-card"
                    onAttach={() => setUploadVisible(true)} onSubmit={() => send(draft, null, attachments)}
                    submitDisabled={!draft.text.trim() && !draft.references.length && !attachments.length} loading={sending} disabled={sending}
                    placeholder={translate(teamMode ? "team.messages.composer-placeholder" : "project.messages.composer-placeholder")}/>
                    : <div className="surface-100 border-round-xl p-3 text-center text-xs text-500">
                        <Icon icon="mi mi:lock" className="ml-2"/>{translate(teamMode ? "team.messages.read-only" : "project.messages.read-only")}
                    </div>}
                {error && <small className="block text-red-500 text-xs mt-2">{translate(error)}</small>}
            </div>
        </div>

        <UploadDialog visible={uploadVisible} setVisible={setUploadVisible} multiple entityType="chat" entityDocumentId={entityKey(chat)}
            purpose="attachment" updateContentType={(files) => setAttachments((current) => {
                const existing = new Set(current.map((item) => String(entityKey(item))));
                return [...current, ...files.filter((item) => !existing.has(String(entityKey(item))))];
            })}/>
    </div>;
}

export default Message;
